import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KNBulletList } from 'components/KNBulletList/KNBulletList'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormFile from 'components/KNForm/KNFormFile'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { COMPANIES_QUERY_KEY, uploadBulkCompanies } from './CompanyManager.service'
import type { BulkCreateCompaniesDialogFormValues } from './CompanyManager.types'

interface BulkCreateCompaniesDialogProps {
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const BulkCreateCompaniesDialog = ({ open, onAction, onClose }: BulkCreateCompaniesDialogProps): ReactElement => {
  const { t } = useTranslation()
  const [createdCompanies, setCreatedCompanies] = useState<string[] | null>(null)
  const { handleSubmit, reset, control, formState, setValue, setError, clearErrors } =
    useForm<BulkCreateCompaniesDialogFormValues>()
  const queryClient = useQueryClient()
  const uploadBulkCompaniesMutation = useMutation({
    mutationFn: uploadBulkCompanies,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          csv: null,
        }),
      )
      setCreatedCompanies(null)
    }
  }, [open])

  const onSubmit: SubmitHandler<BulkCreateCompaniesDialogFormValues> = async (
    data: BulkCreateCompaniesDialogFormValues,
  ) => {
    await handleFormSubmission(async () => {
      setCreatedCompanies(await uploadBulkCompaniesMutation.mutateAsync(data))
    }, {
      onError: (error) => setError('root', processServerErrorMessages(error)),
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={createdCompanies ? onAction : onClose}
      closeLabel={createdCompanies ? t('general.close') : t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.shared.company_manager.bulk_create_companies.bulk_create')}
      actions={
        !createdCompanies && (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {t('general.send')}
          </Button>
        )
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      {createdCompanies ? (
        <DialogContentText component="div">
          {createdCompanies.length == 0 ? (
            <Typography variant="p5" color="text.main">
              {t('screens.shared.company_manager.bulk_create_companies.no_new_companies_created')}
            </Typography>
          ) : (
            <>
              <Typography variant="p5" color="text.main">
                {t('screens.shared.company_manager.bulk_create_companies.created_companies_list')}
              </Typography>
              <KNBulletList sx={{ color: 'text.main' }}>
                {createdCompanies.map((createdCompany) => (
                  <ListItem key={createdCompany}>{createdCompany}</ListItem>
                ))}
              </KNBulletList>
            </>
          )}
        </DialogContentText>
      ) : (
        <Stack spacing={2}>
          <KNFormFile
            label={t('screens.shared.company_manager.bulk_create_companies.choose_file')}
            name="csv"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            accept={['text/csv']}
            size="small"
          />
        </Stack>
      )}
    </KNDialog>
  )
}

export default BulkCreateCompaniesDialog
